import { useCallback, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Declaration } from '../../../../../store/declarations/declaration';
import { declarationIsCompleted } from '../../../utils/declaration-utils';
import { declarationHasBeenSubmitted } from '../../../utils/form-utils';

const VIEW_ONLY_SEGMENT = 'view-only';

const useUpdateViewOfDeclaration = ({
    declaration,
    declarationIsArchived,
}: {
    declaration: Declaration | undefined;
    declarationIsArchived: boolean | undefined;
}) => {
    const location = useLocation();
    const navigate = useNavigate();
    const { declarationId, manifestId, jobId } = useParams();

    const inManifests = Boolean(manifestId && jobId);
    const baseUrl = inManifests ? `manifests/${manifestId}/${jobId}/${declarationId}` : `declarations/${declarationId}`;

    const toViewOnlyUrl = useCallback(
        (pathname: string) => {
            const segments = pathname.split('/').filter(Boolean);
            const baseSegmentsCount = baseUrl.split('/').filter(Boolean).length;

            segments.splice(baseSegmentsCount, 0, VIEW_ONLY_SEGMENT);

            return `/${segments.join('/')}`;
        },
        [baseUrl]
    );

    const fromViewOnlyUrl = useCallback(
        (pathname: string) =>
            `/${pathname
                .split('/')
                .filter(Boolean)
                .filter((segment) => segment !== VIEW_ONLY_SEGMENT)
                .join('/')}`,
        []
    );

    useEffect(() => {
        if (!declaration?.status) return;

        const declarationIsInViewOnlyStatus =
            declarationHasBeenSubmitted(declaration.status) ||
            declarationIsCompleted(declaration) ||
            declarationIsArchived;
        const declarationUrlIsInViewOnly = location.pathname.split('/').includes(VIEW_ONLY_SEGMENT);

        if (declarationIsInViewOnlyStatus && !declarationUrlIsInViewOnly) {
            navigate(toViewOnlyUrl(location.pathname), { replace: true });
        } else if (!declarationIsInViewOnlyStatus && declarationUrlIsInViewOnly) {
            navigate(fromViewOnlyUrl(location.pathname), { replace: true });
        }
    }, [
        declaration,
        declarationId,
        location.pathname,
        navigate,
        declarationIsArchived,
        toViewOnlyUrl,
        fromViewOnlyUrl,
    ]);
};

export default useUpdateViewOfDeclaration;
